import { useLayoutEffect, useState } from 'react';

export const useTruncatedList = <T>(
  containerRef: React.RefObject<HTMLElement | null>,
  items: T[],
  options: { gap?: number } = {},
) => {
  const { gap = 8 } = options;
  const [visibleCount, setVisibleCount] = useState(0);

  const measureVisibleItems = () => {
    const container = containerRef.current;
    if (!container) return items.length;

    const containerWidth = container.clientWidth;
    const children = Array.from(container.children) as HTMLElement[];
    const listItems = children.slice(0, -1);
    const overflowIndicatorWidth = children.at(-1)?.offsetWidth ?? 0;

    if (!containerWidth || !children.length) return items.length;

    let currentWidth = 0;

    for (let i = 0; i < listItems.length; i++) {
      const itemWidth = listItems[i].offsetWidth;
      if (itemWidth === 0) continue;

      if (
        currentWidth + itemWidth + gap + overflowIndicatorWidth >
        containerWidth
      ) {
        return i;
      }
      currentWidth += itemWidth + gap;
    }

    return items.length;
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      requestAnimationFrame(() => {
        setVisibleCount(measureVisibleItems());
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureVisibleItems]);

  return {
    visibleCount,
    hasOverflow: visibleCount < items.length,
  };
};
